import { useCallback, useEffect, useState } from "react";
import { FileDown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { roleLabel } from "@/components/UserMenu";
import { TaskCard } from "@/components/tasks/TaskCard";

export const MeetingReport = ({ onOpen, refreshKey }) => {
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setReport((await api.get("/attendance/meeting-report")).data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  async function exportPdf() {
    setBusy(true);
    try {
      const response = await api.get("/attendance/meeting-report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tuesday-meeting-${report?.through || "report"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p className="form-message error-message" data-testid="meeting-report-error">{error}</p>;
  if (!report) return <p className="projection-loading">Loading the past 7 days…</p>;
  return (
    <section className="task-meeting-report" data-testid="meeting-report">
      <div className="section-heading">
        <div>
          <h3>Past 7 days · {report.since} to {report.through}</h3>
          <p data-testid="meeting-report-totals">{report.totals.completed} completed · {report.totals.overdue} overdue · {report.totals.reopened} reopened</p>
        </div>
        <button type="button" className="button button-secondary" data-testid="meeting-report-pdf" disabled={busy} onClick={exportPdf}><FileDown size={14} /> {busy ? "Preparing…" : "Export PDF"}</button>
      </div>
      <div className="task-meeting">
        {report.owners.map((owner) => (
          <article className="task-meeting-group" key={owner.owner_id} data-testid={`meeting-report-owner-${owner.owner_id}`}>
            <header>
              <div><h3>{owner.owner_name}</h3><p>{roleLabel(owner.owner_role)}</p></div>
              <div className="task-meeting-counts">
                <span className="status-chip status-active" data-testid={`meeting-report-completed-${owner.owner_id}`}>{owner.counts.completed} completed</span>
                <span className={owner.counts.overdue ? "status-chip status-inactive" : "status-chip status-neutral"} data-testid={`meeting-report-overdue-${owner.owner_id}`}>{owner.counts.overdue} overdue</span>
                <span className={owner.counts.reopened ? "status-chip status-review" : "status-chip status-neutral"} data-testid={`meeting-report-reopened-${owner.owner_id}`}>{owner.counts.reopened} reopened</span>
              </div>
            </header>
            {[["Completed", owner.completed], ["Overdue", owner.overdue], ["Reopened", owner.reopened]].map(([label, tasks]) => tasks.length > 0 && (
              <div className="task-meeting-section" key={label}>
                <p className="pairing-meta">{label}</p>
                <div className="task-meeting-items">{tasks.map((task) => <TaskCard key={`${label}-${task.id}`} task={task} onOpen={onOpen} compact />)}</div>
              </div>
            ))}
          </article>
        ))}
        {!report.owners.length && <p className="operations-empty-state" data-testid="meeting-report-empty">No completed, overdue or reopened tasks in the past 7 days.</p>}
      </div>
    </section>
  );
};
